import { lazy, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import { validate as validateUuid } from "uuid";
import classNames from "classnames";

import WaitingIndicator from "@general-ui/waiting-indicator/waiting-indicator";
import blAuth from "connection/bl-auth/bl-auth";
import { loadWorkingEvent, setEditorSize } from "store/actions/admin/create-event";
import { useAppDispatch, useTypedSelector } from "store/reducers/use-typed-selector";
import { useIsRegistrationV2 } from "hooks/registration-hooks";
import { OptionalComponent } from "utils/optional-component";
import { getCreateProjectUrl, getTemplateClassName } from "utils/utils";
import LiveEventDocumentHead from "components/live-event/live-event-document-head";
import { EditorSizes } from "types/template-layouts";
import { getDefaultLanguage } from "components/live-event/utils";
import { LanguagesAbbr } from "types/working-model";
import { useDisableClickEvent } from "hooks/click.hooks";
import useLoadTemplateStyles from "./use-load-template-styles";

import { MarketingRegisteredPage } from "../../../components/live-event/marketing-page/marketing-registered-page";
import useListenForPreviewOverrides from "./use-listen-for-preview-overrides";
const Navbar = lazy(() => import("../../../components/live-event/navbar/navbar"));
const MarketingPage = lazy(() => import('../../../components/live-event/marketing-page/marketing-page'));
const RegistrationWrappedV2 = lazy(() => import("../../../components/live-event/registration-v2/registration-v2"));
const RegistrationWrapped = lazy(() => import("../../../components/live-event/registration/registration"));
const SessionView = lazy(() => import("../../../components/live-event/session/session"));
const PreviewEventChatMain = lazy(() => import("../../../components/live-event/event-chat/preview-event-chat-main"));

const PlatformComponentRenderer = () => {
	const fetching = useRef(false);
	const disableClickerRef = useRef<HTMLDivElement | null>(null);

	const workingEvent = useTypedSelector(state => state.CreateEventReducer.workingEvent);
	const loadingEvent = useTypedSelector(state => state.CreateEventReducer.loadingEvent);
	const editorSize = useTypedSelector(state => state.CreateEventReducer.editorSize);

	const [invalidRendering, setInvalidRendering] = useState(false);
	const [clickDisableRefreshCounter, setClickDisableRefreshCounter] = useState(0);

	// in order to allow api calls access to global user resources from `create.`
	// we need to append the header.
	// Important: this should ONLY happen in this component because this component is rendered
	// as an iframe in project details (create.)
	useEffect(() => {
		const originalFetch = window.fetch;
		window.fetch = async (input, init = {}) => {
			const headers = new Headers(init.headers || {});
			headers.append('bl-global-resource', 'true');
			const newInit = { ...init, headers };
			return originalFetch(input, newInit);
		};

		return () => {
			window.fetch = originalFetch;
		};
	}, []);

	useLoadTemplateStyles();

	const location = useLocation();
	const params: Record<string, string> = useParams();
	const dispatch = useAppDispatch();

	useDisableClickEvent(disableClickerRef, {
		allowedClasses: [
			'navbar__mobile-header-button',
			'navbar__navigation-fixed-button',
			'icon-toggle',
			// dropdowns
			'new-dropdown-value-container',
			'new-dropdown-value',
			'new-dropdown-icon',
			'group-filter-title-container',
			'value-label',
			// calendar toggle
			'icon-toggle-radio',
			'icon-toggle-label',
			// session page content
			'play-pause-toggle',
			'mute-toggle',
			'sliding-menu-option',
			'paused-cover-container',
			'streams-action-button',
			'social-link-icon-container',
			'control-button',
			'reveal-description',
			'navigation-toggle',
			'ev-icon-menu',
			'navbar__overlay',
			'expand-description',
			'user-survey-close-icon',
		],
		includeButtonChildren: true,
		refreshCounter: clickDisableRefreshCounter,
	});

	const isRegistrationV2 = useIsRegistrationV2();

	const workingEventUuid = workingEvent?.uuid;
	const paramsEventUuid = params.id;
	const language = params.language || workingEvent && getDefaultLanguage(workingEvent) || 'en';
	const hash = location.hash;
	const templateClassName = getTemplateClassName(workingEvent?.template.name);

	const firstSessionUuid = workingEvent?.sessions[0]?.uuid;

	useListenForPreviewOverrides();

	useEffect(() => {
		setClickDisableRefreshCounter(prev => prev + 1);
	}, [hash]);

	const isInIframe = useCallback(() => {
		try {
			return window.self !== window.top;
		} catch (e) {
			return true;
		}
	}, []);

	// if we're iframed from the create project app, we don't want to show the preview passcode modal
	const allowPlatformApp = useCallback(() => {
		if (isInIframe() && document.referrer === `${getCreateProjectUrl()}/`) {
			return true;
		}
		return false;
	}, [isInIframe]);

	const sessionUuid = useMemo(() => {
		const regex = /^#path=\/session\/([a-fA-F0-9-]{36})__$/;
		const hashSessionUuid = hash.match(regex)?.[1];
		if (hashSessionUuid && validateUuid(hashSessionUuid)) {
			return hashSessionUuid;
		}
		return firstSessionUuid;
	}, [firstSessionUuid, hash]);

	const session = workingEvent?.sessions.find(s => s.uuid === sessionUuid);

	const activeTab = useMemo(() => {
		if (hash.startsWith(`#path=/session/`)) {
			if (session) {
				return 'session';
			}
		}
		if (hash.startsWith('#path=/registration__')) {
			return 'registration';
		}

		if (hash.startsWith('#path=/landing-page__')) {
			return 'landing-page';
		}
		if (hash.startsWith('#path=/homepage__')) {
			return 'homepage';
		}

		return 'registration';
	}, [hash, session]);

	useEffect(() => {
		// extract __editorSize=<editorSize>__ from the hash and set it in the redux store
		// editor size is either 'desktop' 'tablet' or 'mobile'. IF none, then default to desktop.
		// Also, if registration, always set to desktop. For some reason the UI is just smoother this way... Not sure why.
		if (activeTab === 'registration') {
			dispatch(setEditorSize(EditorSizes.desktop));
			return;
		}
		let editorSize = (hash.split('__editorSize=')[1]?.split('__')[0] || EditorSizes.desktop);
		if (![EditorSizes.desktop, EditorSizes.tablet, EditorSizes.mobile].includes(editorSize as EditorSizes)) {
			editorSize = EditorSizes.desktop;
		}
		dispatch(setEditorSize(editorSize as EditorSizes));
	}, [dispatch, hash, activeTab]);

	useEffect(() => {
		if (fetching.current) return;
		(async () => {
			try {
				// Ensure the token is refreshed before proceeding
				// or we could end up loading the entire login app in the platform template iframe
				await blAuth.refresh();
				const token = blAuth.idToken;
				const invalidEventUuid = !workingEventUuid || (workingEventUuid && workingEventUuid !== paramsEventUuid);
				if (!loadingEvent && invalidEventUuid && paramsEventUuid && token) {
					fetching.current = true;

					const queryParams = new URLSearchParams(window.location.search);
					const isRenderingIntoPlatformAppIframe = queryParams.get('platform_user') === 'true' && allowPlatformApp();
					if (isRenderingIntoPlatformAppIframe) {
						setInvalidRendering(false);
						dispatch(loadWorkingEvent(paramsEventUuid, token, { includeGlobalTemplatesChannel: true }));
					} else {
						setInvalidRendering(true);
					}
				}
			} catch (error) {
				console.error('Error loading working event:', error);
				setInvalidRendering(true);
			}
		})();

	}, [allowPlatformApp, dispatch, loadingEvent, paramsEventUuid, workingEventUuid]);

	if (invalidRendering) return null;
	if (!workingEvent) {
		return (
			<div className="flex-center" style={{ height: '100vh' }}>
				<WaitingIndicator />
			</div>
		);
	}
	return (
		<>
			<LiveEventDocumentHead eventBundle={workingEvent} />
			{/*
				We could use Routes, but switching these routes from the iframe src will force a full iframe re-render,
				which will force this entire app to re-render. Whereas using hash's doesn't.
				However, this means wherever we are accessing session uuids from params, we need to 
				conditionally overwrite them with the hash value if it exists. This is done using the  useSessionUuidParamAdmin hook
			*/}
			<OptionalComponent display={activeTab === 'registration'}>
				<>
					{
						isRegistrationV2 ?
							<RegistrationWrappedV2 isEditor isPlatformIframe /> :
							<RegistrationWrapped />
					}
				</>
			</OptionalComponent>

			<OptionalComponent display={activeTab === 'session' && !!session}>
				<div
					ref={disableClickerRef}
					className={
						classNames(
							'live-event-container',
							editorSize,
							templateClassName,
							templateClassName.toLowerCase(),
							`${templateClassName}-styles`
						)
					}
				>
					<Navbar
						onSession
						canHide
						disableEdit
						isPlatformIframe
					>
						<SessionView
							previewBundle={workingEvent}
							previewSession={session}
							previewLanguage={language as LanguagesAbbr}
							displayEmptyStateIfApplicable={false}
							isPlatformIframe
						/>
						<PreviewEventChatMain isSessionPreview />
					</Navbar>
				</div>
			</OptionalComponent>

			<OptionalComponent display={activeTab === 'landing-page'}>
				<div
					className={classNames(
						'live-event-container',
						editorSize, // not sure if editorSize is needed here, but keeping for now
						templateClassName,
						templateClassName.toLowerCase(),
						`${templateClassName}-styles`,
					)}
					ref={disableClickerRef}
				>
					<MarketingPage
						previewEventBundle={workingEvent}
						editable={false}
					/>
				</div>
			</OptionalComponent>

			<OptionalComponent display={activeTab === 'homepage'}>
				<div
					className={classNames(
						'live-event-container',
						editorSize, // not sure if editorSize is needed here, but keeping for now
						templateClassName,
						templateClassName.toLowerCase(),
						`${templateClassName}-styles`,
					)}
					ref={disableClickerRef}
				>
					<Navbar disableEdit isPlatformIframe>
						<MarketingRegisteredPage isPreview />
					</Navbar>
				</div>
			</OptionalComponent>
		</>
	);
};

export default PlatformComponentRenderer;
